import { Box, Typography } from '@mui/material'
import { GridColDef } from '@mui/x-data-grid-premium'
import { Table } from 'Common/MUITableV2'
import { GridSkeleton } from './GridSkeleton.view'
import { autoHeight } from 'Common/utils/utils'
import { formatInt, useUtilisationData } from './Utilisation.data'
import type { CleaningSummaryRow } from './Utilisation.types'

type Props = {
  schedulerId: string
  iteration: string
}

const COLS: GridColDef<CleaningSummaryRow>[] = [
  { field: 'cip_name', headerName: 'Cleaner', flex: 1, minWidth: 120 },
  {
    field: 'used_hrs',
    headerName: 'Used Hrs',
    type: 'number',
    flex: 1,
    minWidth: 110,
    valueFormatter: (value: number) => formatInt(value),
  },
  {
    field: 'unused_hrs',
    headerName: 'Unused Hrs',
    type: 'number',
    flex: 1,
    minWidth: 110,
    valueFormatter: (value: number) => formatInt(value),
  },
  {
    field: 'available_hrs',
    headerName: 'Available Hrs',
    type: 'number',
    flex: 1,
    minWidth: 130,
    valueFormatter: (value: number) => formatInt(value),
  },
]

export const CleaningSummaryView = ({ schedulerId, iteration }: Props) => {
  const { data, isLoading, error } = useUtilisationData(schedulerId, iteration)

  // Grid skeleton, not a spinner: same columns, same height, so the
  // real table replaces it without the layout moving.
  if (isLoading) return <GridSkeleton columns={COLS as GridColDef[]} />
  if (error || !data) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">
          Could not load utilisation data
          {error instanceof Error ? `: ${error.message}` : ''}.
        </Typography>
      </Box>
    )
  }

  return (
    <Box className="table-container-mui">
      <Table
        ref={(el: HTMLDivElement | null) => el && autoHeight(el)}
        rows={data.cleaning_summary}
        columns={COLS as GridColDef[]}
        getRowId={(r: any) => r.cip_name}
        density="compact"
        disableRowSelectionOnClick
        hideFooter
        getRowClassName={(p) =>
          (p.indexRelativeToCurrentPage ?? 0) % 2 === 0
            ? 'row--even'
            : 'row--odd'
        }
      />
    </Box>
  )
}
